import React, { useState, useEffect, useRef } from 'react';
import { FaBars, FaTicketAlt, FaCheckCircle, FaExchangeAlt } from 'react-icons/fa';
import { db } from '../../../src/admin/servicios/firebase';
import BuscadorViajes from '../componentes/BuscadorViajes';
import vanLogo from '../imgs/vanlogo.png';
import mercedesImage from '../imgs/mercedes.png';
import { getDoc, doc, collection, getDocs, query, where } from 'firebase/firestore';

const limpiarTexto = (texto) => {
  if (!texto) return '';
  return texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
};

// ── Aplica las excepciones de un día a la lista de horarios de una ruta ──────
// Devuelve el array de horas que el cliente debe ver ese día.
const aplicarExcepcion = (horariosSalida, excepcion) => {
  if (!excepcion) return horariosSalida;
  const base = (horariosSalida || []).filter(h => !(excepcion.horariosEliminados || []).includes(h));
  const extra = excepcion.horariosExtra || [];
  return [...new Set([...base, ...extra])].sort();
};

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [renderLoader, setRenderLoader] = useState(true);

  const [mapaZonas, setMapaZonas] = useState({});
  const [rutasDisponibles, setRutasDisponibles] = useState([]);
  const [precios, setPrecios] = useState(null);

  const [viajesDisponibles, setViajesDisponibles] = useState([]);
  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [errorBusqueda, setErrorBusqueda] = useState('');

  const [datosRedondo, setDatosRedondo] = useState(null);
  const [seleccionandoRedondo, setSeleccionandoRedondo] = useState(false);
  const [viajeIdaParaRedondo, setViajeIdaParaRedondo] = useState(null);
  const [opcionesRegreso, setOpcionesRegreso] = useState([]);

  const resultadosRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingConfig(true);
        const termSnap = await getDoc(doc(db, 'configuraciones', 'terminales'));
        if (termSnap.exists()) setMapaZonas(termSnap.data());

        const qRutas = query(collection(db, 'rutas'), where("activo", "==", true));
        const rutasSnap = await getDocs(qRutas);
        setRutasDisponibles(rutasSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const preciosSnap = await getDoc(doc(db, 'configuraciones', 'precios'));
        if (preciosSnap.exists()) setPrecios(preciosSnap.data());
        else console.error('❌ NO EXISTE configuraciones/precios');

        setLoadingConfig(false);
      } catch (err) {
        console.error('❌ ERROR:', err);
        setLoadingConfig(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!loadingConfig) {
      setFadeOut(true);
      const timer = setTimeout(() => setRenderLoader(false), 700);
      return () => clearTimeout(timer);
    }
  }, [loadingConfig]);

  useEffect(() => {
    if (!buscando && (mostrarResultados || errorBusqueda)) {
      setTimeout(() => resultadosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  }, [buscando, mostrarResultados, errorBusqueda]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const calcularLlegada = (hora, duracion) => {
    if (!hora) return "--:--";
    if (!duracion) duracion = 3;
    const [h, m] = hora.split(':').map(Number);
    return `${(h + duracion) % 24}:${m.toString().padStart(2, '0')}`;
  };

  // ── Carga excepciones de una fecha específica desde Firestore ─────────────
  // Devuelve un mapa { rutaId: excepcionData }
  const cargarExcepcionesDia = async (fecha) => {
    try {
      const snap = await getDocs(query(collection(db, 'excepciones'), where('fecha', '==', fecha)));
      const mapa = {};
      snap.forEach(d => { mapa[d.data().rutaId] = d.data(); });
      return mapa;
    } catch (e) {
      console.error('Error cargando excepciones:', e);
      return {};
    }
  };

  // ── Construye las peticiones Firestore para cada horario de una ruta ──────
  const construirPeticionesRuta = (ruta, fecha, horariosAUsar, cantidadPasajeros, precio, fechaHoy, horaActual) => {
  const peticiones = [];
  for (const hora of horariosAUsar) {
    if (fecha === fechaHoy) {
      const [h, m] = hora.split(':').map(Number);
      const minutosDeLaHora = h * 60 + m;
      
      // Si el horario es de madrugada (antes de las 6am) y estamos en la tarde/noche,
      // asumimos que es del día siguiente → no filtrar
      const esMandrugada = h < 6;
      const esNoche = horaActual > 18 * 60; // son más de las 6pm
      
      if (!esMandrugada || !esNoche) {
        if (minutosDeLaHora < (horaActual + 30)) continue;
      }
    }

      const viajeId = `${fecha}_${hora}_${ruta.id}`;
      const peticion = getDoc(doc(db, 'viajes', viajeId)).then(docViaje => {
        let ocupados = 0;
        if (docViaje.exists()) {
          const d = docViaje.data();
          ocupados = (d.ocupados?.length || 0) + (d.bloqueados?.length || 0);
        }
        const libres = 16 - ocupados;
        if (libres >= cantidadPasajeros) {
          return {
            id: viajeId,
            rutaId: ruta.id,
            origen: ruta.origen,
            destino: ruta.destino,
            fecha,
            horaSalida: hora,
            horaLlegada: calcularLlegada(hora, ruta.duracionViaje),
            precio,
            asientosDisponibles: libres
          };
        }
        return null;
      }).catch(() => null);

      peticiones.push(peticion);
    }
    return peticiones;
  };

  // ── Buscar viajes de IDA ───────────────────────────────────────────────────
  const handleBusqueda = async (datos) => {
    setBuscando(true);
    setMostrarResultados(false);
    setErrorBusqueda('');
    setSeleccionandoRedondo(false);
    setViajeIdaParaRedondo(null);

    try {
      const rutasCompatibles = rutasDisponibles.filter(r =>
        limpiarTexto(r.origen) === limpiarTexto(datos.origen) &&
        limpiarTexto(r.destino) === limpiarTexto(datos.destino)
      );

      if (rutasCompatibles.length === 0) {
        setErrorBusqueda("No hay rutas activas para este trayecto.");
        setBuscando(false);
        return;
      }

      const diaSemana = new Date(datos.fecha + "T12:00:00").getDay();
      const ahora = new Date();
      const fechaHoy = ahora.toLocaleDateString('en-CA');
      const horaActual = ahora.getHours() * 60 + ahora.getMinutes();

      // Cargar excepciones para ese día (una sola consulta para todas las rutas)
      const excepcionesDia = await cargarExcepcionesDia(datos.fecha);

      const consultasFirebase = [];

      for (const ruta of rutasCompatibles) {
        if (ruta.diasOperacion && !ruta.diasOperacion.includes(diaSemana)) continue;

        // Aplicar excepción si existe, si no usar horarios base
        const exc = excepcionesDia[ruta.id];
        const horariosAUsar = aplicarExcepcion(ruta.horariosSalida, exc);

        consultasFirebase.push(...construirPeticionesRuta(
          ruta, datos.fecha, horariosAUsar,
          datos.cantidadPasajeros, precios?.precioBase || 0,
          fechaHoy, horaActual
        ));
      }

      const resultados = (await Promise.all(consultasFirebase))
        .filter(v => v !== null)
        .sort((a, b) => a.horaSalida.localeCompare(b.horaSalida));

      setViajesDisponibles(resultados);
      setMostrarResultados(true);
      setDatosRedondo(datos.tipoViaje === 'redondo' ? datos : null);
      if (resultados.length === 0) setErrorBusqueda("No hay salidas disponibles.");

    } catch (error) {
      console.error(error);
      setErrorBusqueda("Error técnico.");
    } finally {
      setBuscando(false);
    }
  };

  // ── Buscar viajes de REGRESO (mismo patrón, aplica excepciones) ───────────
  const buscarViajeRedondo = async (datos, viajeIda) => {
    try {
      const rutasRegreso = rutasDisponibles.filter(r =>
        limpiarTexto(r.origen) === limpiarTexto(datos.destino) &&
        limpiarTexto(r.destino) === limpiarTexto(datos.origen)
      );

      if (rutasRegreso.length === 0) return { error: "No hay rutas de regreso disponibles." };

      const diaSemanaRegreso = new Date(datos.fechaRegreso + "T12:00:00").getDay();
      const ahora = new Date();
      const fechaHoy = ahora.toLocaleDateString('en-CA');
      const horaActual = ahora.getHours() * 60 + ahora.getMinutes();

      // Excepciones para la fecha de regreso
      const excepcionesDia = await cargarExcepcionesDia(datos.fechaRegreso);

      const consultasFirebase = [];

      for (const ruta of rutasRegreso) {
        if (ruta.diasOperacion && !ruta.diasOperacion.includes(diaSemanaRegreso)) continue;

        const exc = excepcionesDia[ruta.id];
        const horariosAUsar = aplicarExcepcion(ruta.horariosSalida, exc);

        consultasFirebase.push(...construirPeticionesRuta(
          ruta, datos.fechaRegreso, horariosAUsar,
          datos.cantidadPasajeros, precios?.precioBase || 0,
          fechaHoy, horaActual
        ));
      }

      const resultadosRegreso = (await Promise.all(consultasFirebase))
        .filter(v => v !== null)
        .sort((a, b) => a.horaSalida.localeCompare(b.horaSalida));

      if (resultadosRegreso.length > 0) {
        return { viajesRegreso: resultadosRegreso, precioRedondo: precios.precioRedondo };
      }

      return { error: "No hay viajes de regreso disponibles." };
    } catch (error) {
      console.error("Error:", error);
      return { error: "Error al buscar viaje de regreso." };
    }
  };

  const iniciarReserva = async (viaje, esRedondo = false) => {
    if (esRedondo && datosRedondo) {
      setViajeIdaParaRedondo(viaje);
      setSeleccionandoRedondo(true);
      const resultadoRedondo = await buscarViajeRedondo(datosRedondo, viaje);
      if (resultadoRedondo.error) {
        alert(`Viaje de ida disponible, pero: ${resultadoRedondo.error}`);
        setSeleccionandoRedondo(false);
        return;
      }
      setOpcionesRegreso(resultadoRedondo.viajesRegreso);
      return;
    }
    localStorage.setItem('viajeSeleccionado', JSON.stringify({
      ...viaje,
      tipoViaje: 'soloIda',
      precioTotal: viaje.precio
    }));
    window.location.href = '/reservar';
  };

  const completarRedondo = (viajeRegreso) => {
    if (!viajeIdaParaRedondo || !viajeRegreso || !precios?.precioRedondo) {
      alert("Error al procesar boleto redondo");
      return;
    }
    localStorage.setItem('viajeSeleccionado', JSON.stringify({
      viajeIda: viajeIdaParaRedondo,
      viajeRegreso: viajeRegreso,
      tipoViaje: 'idaVuelta',
      precioTotal: precios.precioRedondo,
      esRedondo: true,
      fechaIda: viajeIdaParaRedondo.fecha,
      fechaRegreso: viajeRegreso.fecha,
      origenIda: viajeIdaParaRedondo.origen,
      destinoIda: viajeIdaParaRedondo.destino,
      horaSalidaIda: viajeIdaParaRedondo.horaSalida,
      horaLlegadaIda: viajeIdaParaRedondo.horaLlegada,
      origenRegreso: viajeRegreso.origen,
      destinoRegreso: viajeRegreso.destino,
      horaSalidaRegreso: viajeRegreso.horaSalida,
      horaLlegadaRegreso: viajeRegreso.horaLlegada
    }));
    window.location.href = '/reservar';
  };

  const volverASeleccionarIda = () => {
    setSeleccionandoRedondo(false);
    setViajeIdaParaRedondo(null);
  };

  if (!loadingConfig && (!precios || !precios.precioBase)) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">
        <div className="text-center">
          <p className="text-xl font-bold text-red-400">⚠️ Error de configuración</p>
          <p className="text-sm mt-2">No se pudieron cargar los precios del sistema</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {renderLoader && (
        <div className={`fixed inset-0 z-[9999] bg-slate-900 flex items-center justify-center text-white transition-opacity duration-700 ease-in-out ${fadeOut ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-xl font-bold">Bienvenido a bordo</p>
          </div>
        </div>
      )}

      {precios && precios.precioBase && (
        <div className="relative overflow-x-hidden font-sans">
          <section className="relative min-h-screen bg-cover bg-center flex items-center justify-center"
            style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${mercedesImage})` }}>

            <div className="absolute top-0 w-full p-6 flex justify-between items-center z-50">
              <img src={vanLogo} alt="Logo" className="h-12 object-contain rounded-full bg-white/10 p-1 border border-white/20 shadow-sm" />
              <button onClick={toggleMenu} className="md:hidden text-white text-2xl z-50 relative"><FaBars /></button>
              <div className="hidden md:flex gap-8 items-center">
                <a href="/" className="text-white hover:text-blue-400 font-medium">Inicio</a>
                <a href="/ubicaciones" className="text-white hover:text-blue-400 font-medium">Ubicaciones</a>
                <a href="/mis-viajes" className="flex items-center gap-2 text-emerald-400 border border-emerald-400 px-4 py-2 rounded-full font-bold hover:bg-emerald-400/10">
                  <FaTicketAlt /> Mis Viajes
                </a>
              </div>
              {isMenuOpen && (
                <div className="md:hidden fixed inset-0 bg-slate-900/95 backdrop-blur-sm z-40">
                  <div className="flex flex-col items-center justify-center h-full gap-8">
                    <button onClick={toggleMenu} className="absolute top-6 right-6 text-white text-3xl">✕</button>
                    <a href="/" onClick={() => setIsMenuOpen(false)} className="text-white text-2xl font-bold">Inicio</a>
                    <a href="/ubicaciones" onClick={() => setIsMenuOpen(false)} className="text-white text-2xl font-bold">Ubicaciones</a>
                    <a href="/mis-viajes" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 text-emerald-400 border-2 border-emerald-400 px-8 py-4 rounded-full text-xl font-bold">
                      <FaTicketAlt /> Mis Viajes
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="relative z-10 w-full max-w-5xl px-4 mt-20">
              <h1 className="text-4xl md:text-6xl font-black text-center text-white mb-4 drop-shadow-lg">
                Viaja con <span className="text-blue-500">Ángeles del Puerto</span>
              </h1>

              <BuscadorViajes mapaZonas={mapaZonas} onBuscar={handleBusqueda} />

              <div ref={resultadosRef} className="scroll-mt-24"></div>

              {buscando && <div className="mt-8 text-center text-white font-bold animate-pulse">🔎 Buscando rutas...</div>}
              {errorBusqueda && <div className="mt-8 bg-red-500/80 p-4 rounded-lg text-white text-center font-bold">{errorBusqueda}</div>}

              {mostrarResultados && !seleccionandoRedondo && !buscando && (
                <div className="mt-10 bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6 md:p-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <FaCheckCircle className="text-emerald-400" />
                    {datosRedondo ? 'Selecciona tu viaje de ida' : 'Horarios Disponibles'} ({viajesDisponibles.length})
                  </h3>
                  <div className="grid gap-4">
                    {viajesDisponibles.map((viaje) => (
                      <div key={viaje.id} className="bg-white/90 p-5 rounded-2xl flex flex-col md:flex-row justify-between items-center gap-4 shadow-lg hover:scale-[1.01] transition-transform">
                        <div className="text-center md:text-left">
                          <div className="text-3xl font-black text-slate-800">{viaje.horaSalida}</div>
                          <div className="text-slate-500 text-sm font-medium mt-1">Llegada aprox: {viaje.horaLlegada}</div>
                          <div className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded inline-block mt-2 font-bold">
                            {viaje.asientosDisponibles} asientos libres
                          </div>
                        </div>
                        <div className="text-center md:text-right">
                          <div className="text-3xl font-black text-blue-600">
                            ${viaje.precio}{datosRedondo ? ' (ida)' : ''}
                          </div>
                          <button
                            onClick={() => datosRedondo ? iniciarReserva(viaje, true) : iniciarReserva(viaje)}
                            className="bg-black text-white px-8 py-3 rounded-xl font-bold shadow-lg hover:bg-gray-800 transition-all"
                          >
                            {datosRedondo ? 'Seleccionar Ida' : 'Seleccionar'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {seleccionandoRedondo && viajeIdaParaRedondo && (
                <div className="mt-10 bg-blue-500/10 backdrop-blur-md border border-blue-500/30 rounded-3xl p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                      <FaExchangeAlt className="text-blue-400" /> Selecciona tu viaje de regreso
                    </h3>
                    <button onClick={volverASeleccionarIda} className="text-white hover:text-blue-300 text-sm font-medium">
                      ← Cambiar viaje de ida
                    </button>
                  </div>

                  <div className="mb-6 p-4 bg-blue-500/20 border border-blue-400/30 rounded-xl">
                    <h4 className="text-white font-bold mb-2">Viaje de ida seleccionado:</h4>
                    <div className="flex justify-between items-center text-white">
                      <div>
                        <span className="font-bold">{viajeIdaParaRedondo.horaSalida}</span> → {viajeIdaParaRedondo.horaLlegada}
                        <span className="ml-4 text-sm">Precio: ${viajeIdaParaRedondo.precio}</span>
                      </div>
                      <div className="text-sm">{viajeIdaParaRedondo.fecha}</div>
                    </div>
                  </div>

                  <div className="mb-6 p-4 bg-green-500/20 border border-green-400/30 rounded-xl">
                    <div className="text-white text-center">
                      <span className="font-bold text-lg">Precio especial boleto redondo: </span>
                      <span className="text-2xl font-black text-green-300">${precios.precioRedondo}</span>
                    </div>
                  </div>

                  <div className="grid gap-4">
                    {opcionesRegreso.length > 0 ? (
                      opcionesRegreso.map((viajeRegreso) => (
                        <div key={viajeRegreso.id} className="bg-white/90 p-5 rounded-2xl flex flex-col md:flex-row justify-between items-center gap-4 shadow-lg">
                          <div className="text-center md:text-left">
                            <div className="text-3xl font-black text-slate-800">{viajeRegreso.horaSalida}</div>
                            <div className="text-slate-500 text-sm font-medium mt-1">Llegada aprox: {viajeRegreso.horaLlegada}</div>
                            <div className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded inline-block mt-2 font-bold">
                              {viajeRegreso.asientosDisponibles} asientos libres • {viajeRegreso.fecha}
                            </div>
                          </div>
                          <div className="text-center md:text-right">
                            <div className="mb-2">
                              <div className="text-sm text-slate-600">Precio individual: ${viajeRegreso.precio}</div>
                              <div className="text-2xl font-black text-green-600">Total redondo: ${precios.precioRedondo}</div>
                            </div>
                            <button
                              onClick={() => completarRedondo(viajeRegreso)}
                              className="bg-green-600 text-white px-8 py-3 rounded-xl font-bold shadow-lg hover:bg-green-700 transition-all"
                            >
                              Seleccionar Regreso
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center p-6 bg-white/80 rounded-xl">
                        <p className="text-red-600 font-bold mb-4">No hay viajes de regreso disponibles.</p>
                        <button onClick={volverASeleccionarIda} className="bg-black text-white px-6 py-2 rounded-lg font-bold">
                          ← Volver a seleccionar ida
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </>
  );
}